import React, {useState, ReactNode} from 'react';
import Checkbox from 'shared/components/checkbox';
import Tooltip from 'src/components/tooltip';

interface StatefulCheckboxProps {
  initialChecked: boolean;
  onToggle: (newValue: boolean) => Promise<void>;
  disabled?: boolean;
  disabledTooltip?: string;
  className?: string;
  wrapper?: (checkbox: ReactNode) => ReactNode;
}

const StatefulCheckbox = ({
  initialChecked,
  onToggle,
  disabled = false,
  disabledTooltip,
  className,
  wrapper,
}: StatefulCheckboxProps) => {
  const [checked, setChecked] = useState(initialChecked);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleChange = async () => {
    if (isUpdating || disabled) return;

    const newValue = !checked;
    setChecked(newValue);
    setIsUpdating(true);

    try {
      await onToggle(newValue);
    } catch {
      setChecked(!newValue);
    } finally {
      setIsUpdating(false);
    }
  };

  const checkboxElement = (
    <Checkbox
      checked={checked}
      onChange={handleChange}
      disabled={isUpdating || disabled}
      className={className}
    />
  );

  const wrappedCheckbox = wrapper ? wrapper(checkboxElement) : checkboxElement;

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip}>
        <span>{wrappedCheckbox}</span>
      </Tooltip>
    );
  }

  return <>{wrappedCheckbox}</>;
};

export default StatefulCheckbox;
